/**
 * pdfjs.ts — the portal's ONE pdf.js entry point (BACKLOG-1539).
 *
 * Why this exists: the kiosk used to hand PDFs to an <iframe>, which depends on
 * the BROWSER'S built-in PDF viewer. Bank group policy commonly turns that off
 * (AlwaysOpenPdfExternally), so the signer got a "not available" icon where the
 * disclosure should have been. We now rasterize the PDF ourselves with pdf.js,
 * which works regardless of browser PDF policy.
 *
 * NO CDN (CLAUDE.md): `pdfjs-dist` is an npm dependency of this app and the
 * worker is imported with Vite's `?url`, which emits it as a hashed asset next
 * to our own bundles and returns a SAME-ORIGIN url. Nothing is fetched from
 * mozilla.github.io / unpkg / jsdelivr, so the whole thing works air-gapped.
 *
 * CSP: the worker is same-origin, so it is allowed by `script-src 'self'`
 * (worker-src falls back to script-src). No CSP change was needed.
 *
 * LEGACY build on purpose: `pdfjs-dist/build/*` (the "modern" build) calls very
 * new platform APIs unguarded — e.g. `Map.prototype.getOrInsertComputed` — and
 * throws on anything but a current browser. This surface is customer-facing
 * (in-branch kiosk AND the remote signing link on the customer's own device), so
 * it takes the polyfilled `legacy/` build: ~60 KB more, renders on old Edge /
 * older iOS Safari instead of failing. Sibling apps (ROP-teams, risk-mgmt-panel)
 * run in a controlled browser and keep the modern build.
 *
 * Not bundled, deliberately: cMaps (CJK), standard-font data and the wasm image
 * decoders (JPX / JBIG2 / CCITT). A scan of all 81 seed PCB forms in
 * docs/forms-library-seeds found embedded fonts in every one, images only as
 * DCTDecode (JPEG, decoded by pdf.js itself) and zero JPX/JBIG2/CCITT — so none
 * of those extra payloads (and, for wasm, a `'wasm-unsafe-eval'` CSP relaxation)
 * buys anything today. If a future document needs them, add the assets and the
 * matching `cMapUrl` / `standardFontDataUrl` / `wasmUrl` options here, in one
 * place.
 */

type PdfJsModule = typeof import('pdfjs-dist/legacy/build/pdf.mjs');

export type PdfDocumentProxy = Awaited<ReturnType<PdfJsModule['getDocument']>['promise']>;
export type PdfPageProxy = Awaited<ReturnType<PdfDocumentProxy['getPage']>>;

let modulePromise: Promise<PdfJsModule> | null = null;

/**
 * Lazily import pdf.js (keeps ~500 KB off the portal's critical path) and point
 * it at the bundled worker. Memoized — the worker is configured exactly once.
 * A failed load is NOT cached, so a transient chunk-load error can be retried.
 */
export async function loadPdfJs(): Promise<PdfJsModule> {
  if (!modulePromise) {
    const attempt = (async (): Promise<PdfJsModule> => {
      const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
      const workerUrl: string = (
        await import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url')
      ).default;
      pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
      return pdfjs;
    })();
    attempt.catch(() => {
      modulePromise = null;
    });
    modulePromise = attempt;
  }
  return modulePromise;
}
